// ============================================================
// insightDigest.js — Phase 2 Supabase-native implementation
// Crestview Eighty HOA Portal
//
// Reads from:
//   - rag_query_log (primary, richer data — Phase 2+)
//   - activity_events where event_type='rag_query' (fallback, pre-Phase-2)
// Deduplicates by query string + timestamp within 5 seconds.
// ============================================================

// —— Topic buckets (matching existing UI) —————————————————————
const TOPIC_BUCKETS = [
  {
    id: 'architectural',
    label: 'Architectural & ACC',
    keywords: [
      'setback', 'fence', 'shed', 'garage', 'roof', 'deck', 'addition',
      'building', 'structure', 'paint', 'color', 'outbuilding', 'accessory',
      'height', 'siding', 'metal',
    ],
  },
  {
    id: 'financial',
    label: 'Dues & Finances',
    keywords: [
      'dues', 'assessment', 'fee', 'budget', 'reserve', 'payment',
      'delinquent', 'balance', 'fine', 'penalty', 'cost', 'charge',
    ],
  },
  {
    id: 'governance',
    label: 'Governance & Meetings',
    keywords: [
      'vote', 'board', 'meeting', 'resolution', 'bylaw', 'quorum',
      'election', 'proxy', 'amendment', 'minutes', 'agenda',
    ],
  },
  {
    id: 'legal',
    label: 'Legal & Enforcement',
    keywords: [
      'enforce', 'violation', 'attorney', 'liability', 'dispute', 'lien',
      'lawsuit', 'penalty', 'rule',
    ],
  },
  {
    id: 'general',
    label: 'General',
    keywords: [], // catch-all
  },
];

function bucketQuery(queryText) {
  const lower = (queryText || '').toLowerCase();
  for (const bucket of TOPIC_BUCKETS) {
    if (bucket.keywords.some(kw => lower.includes(kw))) {
      return bucket.id;
    }
  }
  return 'general';
}

// —— Deduplication ————————————————————————————————————————————
// Deduplicate by query string + timestamp within 5 seconds.
function deduplicateEntries(entries) {
  const seen = new Map(); // key → best entry
  for (const e of entries) {
    const ts = new Date(e.created_at || e.occurred_at).getTime();
    const bucketTs = Math.floor(ts / 5000); // 5-second bucket
    const key = `${(e.query || '').trim().toLowerCase()}::${bucketTs}`;
    if (!seen.has(key)) {
      seen.set(key, e);
    } else {
      // Prefer rag_query_log entries (they have more fields)
      const existing = seen.get(key);
      if (!existing._from_rag_log && e._from_rag_log) {
        seen.set(key, e);
      }
    }
  }
  return [...seen.values()];
}

// —— Window filter ————————————————————————————————————————————
function filterByWindow(entries, windowDays, startDate, endDate) {
  if (windowDays === 'custom' && startDate && endDate) {
    const s = new Date(startDate);
    const e = new Date(endDate);
    e.setHours(23, 59, 59, 999);
    return entries.filter(entry => {
      const d = new Date(entry.created_at || entry.occurred_at);
      return d >= s && d <= e;
    });
  }
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - windowDays);
  return entries.filter(e =>
    new Date(e.created_at || e.occurred_at) >= cutoff
  );
}

// —— Data fetching ————————————————————————————————————————————
async function fetchRagQueryLog(supabase, hoaId, windowDays, startDate, endDate) {
  let from;
  if (windowDays === 'custom' && startDate) {
    from = new Date(startDate).toISOString();
  } else {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (windowDays || 30));
    from = cutoff.toISOString();
  }

  const { data, error } = await supabase
    .from('rag_query_log')
    .select('query, answer, intent_class, chunks_used, input_tokens, estimated_cost_usd, cache_hit, user_rating, user_role, created_at')
    .eq('hoa_id', hoaId)
    .gte('created_at', from)
    .order('created_at', { ascending: false })
    .limit(1000);

  if (error) {
    console.warn('rag_query_log fetch failed:', error.message);
    return [];
  }
  return (data || []).map(r => ({ ...r, _from_rag_log: true }));
}

async function fetchActivityEvents(supabase, hoaId, windowDays, startDate, endDate) {
  let from;
  if (windowDays === 'custom' && startDate) {
    from = new Date(startDate).toISOString();
  } else {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (windowDays || 30));
    from = cutoff.toISOString();
  }

  const { data, error } = await supabase
    .from('activity_events')
    .select('metadata, occurred_at, user_id')
    .eq('hoa_id', hoaId)
    .eq('event_type', 'rag_query')
    .gte('occurred_at', from)
    .order('occurred_at', { ascending: false })
    .limit(1000);

  if (error) {
    console.warn('activity_events fetch failed:', error.message);
    return [];
  }

  // Normalize to common shape
  return (data || []).map(r => ({
    query: r.metadata?.query || '',
    answer: r.metadata?.answer || '',
    intent_class: r.metadata?.intent_class || 'general',
    chunks_used: r.metadata?.chunks_used || 0,
    input_tokens: 0,
    estimated_cost_usd: r.metadata?.cost_usd || 0,
    cache_hit: r.metadata?.cache_hit || false,
    user_rating: null,
    user_role: r.metadata?.user_role || 'unknown',
    created_at: r.occurred_at,
    _from_rag_log: false,
  }));
}

async function fetchBudget(supabase, hoaId) {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  let data = null;
  try {
    const result = await supabase
      .from('rag_budget')
      .select('estimated_cost_usd, hard_limit_usd, warning_threshold_usd, is_limit_reached')
      .eq('hoa_id', hoaId)
      .eq('month', month)
      .eq('year', year)
      .maybeSingle();
    data = result.data;
  } catch { /* treat missing row as null */ }

  const spent = data?.estimated_cost_usd || 0;
  const limit = data?.hard_limit_usd || 10;
  const warning = data?.warning_threshold_usd || 8;
  const percentUsed = limit > 0 ? Math.round((spent / limit) * 100) : 0;
  const costPerQuery = 0.002; // ~$0.002/query (haiku estimate)
  const remaining = Math.max(0, limit - spent);
  const estimatedQueriesRemaining = costPerQuery > 0
    ? Math.floor(remaining / costPerQuery)
    : 5000;

  return {
    spent,
    limit,
    warning_threshold: warning,
    is_limit_reached: data?.is_limit_reached || false,
    percentUsed,
    estimatedQueriesRemaining,
    month,
    year,
  };
}

// —— Core generate ————————————————————————————————————————————
async function generate(supabase, hoaId, windowDays = 30, startDate = null, endDate = null) {
  // Fetch from both sources in parallel
  const [ragRows, activityRows, budget] = await Promise.all([
    fetchRagQueryLog(supabase, hoaId, windowDays, startDate, endDate),
    fetchActivityEvents(supabase, hoaId, windowDays, startDate, endDate),
    fetchBudget(supabase, hoaId),
  ]);

  // Merge and deduplicate
  const allRows = deduplicateEntries([...ragRows, ...activityRows]);
  const windowRows = filterByWindow(allRows, windowDays, startDate, endDate);

  // —— Unanswered ——————————————————————————————————————————————
  const UNANSWERED_MARKERS = [
    "isn't in the documents",
    'not in the documents',
    'Unable to answer',
    'No source found',
    'temporarily unavailable',
    'contact the board',
    'not in the provided passages',
  ];
  const unansweredRows = windowRows.filter(r => {
    const a = (r.answer || '').toLowerCase();
    return !r.answer || UNANSWERED_MARKERS.some(m => a.includes(m.toLowerCase()));
  });

  const unanswered_queries = unansweredRows
    .slice(0, 10)
    .map(r => ({
      query: r.query,
      occurred_at: r.created_at || r.occurred_at,
      // keep timestamp for UI that uses q.timestamp
      timestamp: r.created_at || r.occurred_at,
    }));

  // —— Topic bucketing ————————————————————————————————————————
  const topicCounts = {};
  const topicQueries = {};
  for (const bucket of TOPIC_BUCKETS) {
    topicCounts[bucket.id] = 0;
    topicQueries[bucket.id] = [];
  }
  for (const r of windowRows) {
    const id = bucketQuery(r.query);
    topicCounts[id] = (topicCounts[id] || 0) + 1;
    topicQueries[id] = topicQueries[id] || [];
    topicQueries[id].push(r);
  }

  const top_topics = TOPIC_BUCKETS
    .map(b => ({
      id: b.id,
      label: b.label,
      keywords: b.keywords,
      count: topicCounts[b.id] || 0,
      queries: topicQueries[b.id] || [],
      sample_queries: (topicQueries[b.id] || [])
        .slice(0, 3)
        .map(r => r.query),
    }))
    .filter(b => b.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // —— Summary ————————————————————————————————————————————————
  const total_queries = windowRows.length;
  const unanswered_count = unansweredRows.length;
  const answered_count = total_queries - unanswered_count;
  const answer_rate = total_queries > 0
    ? Math.round((answered_count / total_queries) * 100)
    : 0;

  const ragLogRows = windowRows.filter(r => r._from_rag_log);
  const avg_tokens = ragLogRows.length > 0
    ? Math.round(ragLogRows.reduce((s, r) => s + (r.input_tokens || 0), 0) / ragLogRows.length)
    : 0;
  const total_cost_usd = ragLogRows.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0);
  const cache_hits = ragLogRows.filter(r => r.cache_hit).length;
  const cache_hit_rate = ragLogRows.length > 0
    ? Math.round((cache_hits / ragLogRows.length) * 100)
    : 0;

  // flagged = rows where user_rating === false (thumbs down)
  const flagged_queries = ragLogRows.filter(r => r.user_rating === false).length;

  // —— Agenda suggestions ————————————————————————————————————
  const agenda_suggestions = [
    ...(top_topics.length > 0 && top_topics[0].count >= 3
      ? [{
        type: 'trending_topic',
        priority: 'medium',
        title: `Consider communication about ${top_topics[0].label}`,
        detail: `${top_topics[0].count} member queries about ${top_topics[0].label.toLowerCase()} this period. A member communication or document update may reduce support volume.`,
        queries: top_topics[0].sample_queries,
      }]
      : []
    ),
    ...(unanswered_count >= 1
      ? [{
        type: 'document_gap',
        priority: 'high',
        title: 'Document Gap: Unanswered Questions',
        detail: `${unanswered_count} question${unanswered_count === 1 ? '' : 's'} could not be answered from current documents. Upload relevant governing documents or meeting minutes.`,
        queries: unanswered_queries.slice(0, 3).map(q => q.query),
      }]
      : []
    ),
    ...(flagged_queries > 0
      ? [{
        type: 'quality_concern',
        priority: 'medium',
        title: 'AI Answer Quality Concern',
        detail: `${flagged_queries} answer${flagged_queries === 1 ? '' : 's'} received a thumbs-down rating from members. Review and consider adding more precise source documents.`,
      }]
      : []
    ),
  ];

  // —— Window label ————————————————————————————————————————————
  const window_label = windowDays === 'custom'
    ? `Custom Range (${startDate} to ${endDate})`
    : windowDays === 7 ? 'Last 7 days'
    : windowDays === 30 ? 'Last 30 days'
    : `Last ${windowDays} days`;

  return {
    generated_at: new Date().toISOString(),
    window_days: windowDays,
    window_label,
    summary: {
      total_queries,
      unique_users: 0, // not yet tracked
      unanswered_count,
      // Legacy fields kept so existing UI doesn't break
      unanswered: unanswered_count,
      answered: answered_count,
      answer_rate,
      flagged_queries,
      high_risk_flags: 0,
      unreviewed_flags: 0,
      avg_tokens,
      total_cost_usd,
      cache_hit_rate,
    },
    top_topics,
    unanswered_queries,
    agenda_suggestions,
    budget,
    role_breakdown: {}, // not yet implemented
  };
}

// —— Quick stats ——————————————————————————————————————————————
async function getQuickStats(supabase, hoaId) {
  const [digest30, digest7] = await Promise.all([
    generate(supabase, hoaId, 30),
    generate(supabase, hoaId, 7),
  ]);
  return {
    monthly: digest30.summary,
    weekly: digest7.summary,
    top_topic_this_week: digest7.top_topics[0] || null,
    agenda_items: digest30.agenda_suggestions.length,
  };
}

export const insightDigest = { generate, getQuickStats };
